const moment = require('moment');
const {
    EMAIL_RE,
    STORAGE_DIR_NAME,
    ROLES,
    STATUS,
    AREA_CODE_MAX_LEN,
    NATIONAL_NUMBER_MAX_LEN,
    NAME_MAX_LEN,
    EMAIL_MAX_LEN
} = require('./UserConstant')
const {COUNTRIES} = require("../app/AppConstant");
const {fsMomentImport} = require("../utils/utils")

class User {

    constructor({
                    id = '',
                    additionalID = {},
                    role = ROLES.CUSTOMER,
                    status = STATUS.ACTIVE,
                    email = '',
                    firstName = '',
                    lastName = '',
                    photoURL = '',
                    hasPhoto = false,
                    phoneAreaCode = COUNTRIES['DE'].phone,
                    phoneNational = '',
                    birthday = null,
                    customerID = '',
                    created = moment(),
                } = {}) {

        this.id = id;
        this.additionalID = additionalID;
        this.role = role;
        this.status = status;
        this._email = email;
        this._firstName = firstName;
        this._lastName = lastName;
        this.photoURL = photoURL;
        this.hasPhoto = hasPhoto;
        this._phoneAreaCode = phoneAreaCode;
        this._phoneNational = phoneNational;
        birthday = fsMomentImport(birthday) // Moment.js does not support Timestamp object (currently used by Firestore)
        this.birthday = birthday && birthday.isValid() ? birthday : null;
        created = fsMomentImport(created) // Moment.js does not support Timestamp object (currently used by Firestore)
        this.created = created && created.isValid() ? created : moment();
        this.customerID = customerID;
    }


    get email() {
        return this._email;
    }

    set email(value) {
        if (!value) {
            this._email = '';
        }
        if (typeof value === 'string' && value.length < EMAIL_MAX_LEN) {
            this._email = value;
        }
    }

    get firstName() {
        return this._firstName;
    }

    set firstName(value) {
        if (!value) {
            this._firstName = '';
        }
        if (typeof value === 'string' && value.length < NAME_MAX_LEN) {
            this._firstName = value;
        }
    }

    get lastName() {
        return this._lastName;
    }

    set lastName(value) {
        if (!value) {
            this._lastName = '';
        }
        if (typeof value === 'string' && value.length < NAME_MAX_LEN) {
            this._lastName = value;
        }
    }

    get phoneAreaCode() {
        return this._phoneAreaCode;
    }

    set phoneAreaCode(value) {
        if (!value) {
            this._phoneAreaCode = ''
        }
        if (typeof value === 'string' && value.length < AREA_CODE_MAX_LEN) {
            // only keep digits
            this._phoneAreaCode = value.replace(/\D/g, '');
            // remove preceding zeros
            this._phoneAreaCode = this._phoneAreaCode.replace(/^[0]+/, '');
        }
    }

    get phoneNational() {
        return this._phoneNational;
    }

    set phoneNational(value) {
        if (!value) {
            this._phoneNational = ''
        }
        if (typeof value === 'string' && value.length < NATIONAL_NUMBER_MAX_LEN) {
            // only keep digits
            this._phoneNational = value.replace(/\D/g, '');
            // remove preceding zeros
            this._phoneNational = this._phoneNational.replace(/^[0]+/, '');
        }
    }

    static get FS_COLLECTION_NAME() {
        return 'user'
    }

    static get STORAGE_DIR_NAME() {
        return STORAGE_DIR_NAME
    }

    static get ROLES() {
        return ROLES
    };

    static get STATUS() {
        return STATUS
    };

    validate(attribute) {

        if (attribute === undefined) {
            const attributes = Object.keys(this);

            try {
                attributes.map(attribute => {
                    if (!this._validate(attribute)) {
                        console.log(attribute)
                        throw "validation error"
                    }
                });
            } catch (e) {
                return false
            }

            return true
        }

        return this._validate(attribute)
    }

    _validate(attribute) {

        try {
            switch (attribute.replace('_', '')) {
                case 'id':
                case 'customerID':
                    return (typeof this[attribute] === 'string');
                case 'role':
                    return Object.values(ROLES).includes(this[attribute])
                case 'status':
                    return Object.values(STATUS).includes(this[attribute]);
                case 'email':
                    return EMAIL_RE.test(String(this[attribute]).toLowerCase())
                        && (this[attribute].length <= 255);
                case 'firstName':
                case 'lastName':
                    return (typeof this[attribute] === 'string')
                        && (this[attribute].length !== 0)
                        && (this[attribute].length <= 50);
                case 'photoURL':
                    return (typeof this[attribute] === 'string')
                        && (this[attribute].length <= 1024);
                case 'hasPhoto':
                    return typeof this[attribute] === 'boolean';
                case 'phoneAreaCode':
                    return Object.values(COUNTRIES).some((c) => c.phone === this[attribute])
                case 'phoneNational':
                    return (typeof this[attribute] === 'string')
                        && (this[attribute].length !== 0)
                        && (this[attribute].length <= 15);
                case 'created':
                case 'birthday':
                    return this[attribute] === null || this[attribute] instanceof moment
                case 'additionalID':
                    return true;
                default:
                    return false
            }
        } catch (e) {
            console.error('Validating User with error:', e, attribute);
            return false
        }
    }

    get initials() {
        return this.firstName.substring(0, 1) + this.lastName.substring(0, 1)
    }

    get fullName() {
        return `${this.firstName} ${this.lastName}`
    }

    get phone() {
        return `+${this.phoneAreaCode} ${this.phoneNational}`
    }

    get imageStorageRef() {
        return `${STORAGE_DIR_NAME}/${this.id}.jpg`
    }

    toFirestore() {

        return {
            id: this.id,
            additionalID: this.additionalID,
            role: this.role,
            status: this.status,
            email: this.email.trim().toLowerCase(),
            firstName: this.firstName.trim(),
            lastName: this.lastName.trim(),
            hasPhoto: this.hasPhoto,
            phoneAreaCode: this.phoneAreaCode.trim(),
            phoneNational: this.phoneNational.trim(),
            birthday: this.birthday && (typeof this.birthday.toDate === 'function') ? this.birthday.toDate() : null,
            created: this.created && (typeof this.created.toDate === 'function') ? this.created.toDate() : null,
            customerID: this.customerID,
        }
    }

    toFbAuth() {
        return {
            email: this.email,
            displayName: this.fullName,
            phoneNumber: this.phone,
        }
    }

    static parseFirestore(user, key) {
        if (key) {
            user.id = key;
        }
        return new User(user)
    }

    static fromFirestore(fsUsers) {

        let users = {};
        if (fsUsers) {
            Object.keys(fsUsers).forEach((key) => {
                // the firestore object is not null
                if (fsUsers[key]) {
                    users[key] = User.parseFirestore(fsUsers[key], key)
                }
            });
        }

        return users
    }

    isSame(user) {

        return Object.is(
            JSON.stringify(user),
            JSON.stringify(this))
    }
}

module.exports = User;